import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const ShoppingCart = () => {
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(3);
  const [user, setUser] = useState(null);
  const [cartItems, setCartItems] = useState([
    {
      id: 1,
      name: 'Express International Air',
      subtitle: 'Priority air freight',
      service: 'Air Cargo - Priority',
      origin: 'New York, NY',
      destination: 'London, UK',
      weight: '25 kg',
      dimensions: '60x40x30 cm',
      price: 299.99,
      quantity: 1,
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158',
      imageAlt: 'Commercial cargo airplane for express international air freight service',
      deliveryTime: '2-3 business days'
    },
    {
      id: 2,
      name: 'Ocean Freight Container',
      subtitle: 'Full container load',
      service: 'FCL - 20ft Container',
      origin: 'Shanghai, China',
      destination: 'Los Angeles, CA',
      weight: '15000 kg',
      dimensions: '20ft Container',
      price: 1499.99,
      quantity: 1,
      image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec',
      imageAlt: 'Cargo container ship for ocean freight shipping service',
      deliveryTime: '15-20 business days'
    },
    {
      id: 3,
      name: 'Same-Day Local Delivery',
      subtitle: 'Ultra-fast local service',
      service: 'Express Local',
      origin: 'Manhattan, NY',
      destination: 'Brooklyn, NY',
      weight: '5 kg',
      dimensions: '30x20x15 cm',
      price: 49.99,
      quantity: 2,
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d',
      imageAlt: 'Delivery courier for same-day express local delivery service',
      deliveryTime: 'Same day'
    }
  ]);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);

  // Mock user data
  React.useEffect(() => {
    const mockUser = localStorage.getItem('currentUser');
    if (mockUser) {
      setUser(JSON.parse(mockUser));
    }
  }, []);

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems(prevItems =>
      prevItems?.map(item =>
        item?.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const handleRemoveItem = (itemId) => {
    setCartItems(prevItems => prevItems?.filter(item => item?.id !== itemId));
    setCartCount(prev => Math.max(0, prev - 1));
  };

  const handleApplyPromo = () => {
    // Mock promo code validation
    const validPromoCodes = {
      'SHIP10': { discount: 0.10, description: '10% off shipping' },
      'EXPRESS20': { discount: 0.20, description: '20% off express services' },
      'FREESHIP': { discount: 50, description: '$50 off shipping', type: 'fixed' }
    };

    if (validPromoCodes?.[promoCode?.toUpperCase()]) {
      setAppliedPromo(validPromoCodes?.[promoCode?.toUpperCase()]);
    } else {
      alert('Invalid promo code');
    }
  };

  const calculateSubtotal = () => {
    return cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
  };

  const calculateDiscount = () => {
    if (!appliedPromo) return 0;
    const subtotal = calculateSubtotal();
    if (appliedPromo?.type === 'fixed') {
      return appliedPromo?.discount;
    }
    return subtotal * appliedPromo?.discount;
  };

  const calculateInsurance = () => {
    return calculateSubtotal() * 0.02; // 2% insurance
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount() + calculateInsurance();
  };

  const handleCheckout = () => {
    if (cartItems?.length === 0) {
      alert('Your quote cart is empty');
      return;
    }
    // Navigate to checkout or booking confirmation
    navigate('/table-reservation', { state: { cartItems, total: calculateTotal() } });
  };

  const handleCartClick = () => {
    navigate('/shopping-cart');
  };

  const handleAccountClick = (action) => {
    switch (action) {
      case 'login': navigate('/login');
        break;
      case 'register': navigate('/register');
        break;
      case 'account': navigate('/user-account');
        break;
      case 'logout': localStorage.removeItem('currentUser');
        setUser(null);
        break;
      default:
        break;
    }
  };

  const handleSearch = (searchTerm) => {
    navigate('/menu-catalog', { state: { searchQuery: searchTerm } });
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    setUser(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        cartCount={cartCount}
        user={user}
        onCartClick={handleCartClick}
        onAccountClick={handleAccountClick}
        onSearch={handleSearch}
        onLogout={handleLogout}
      />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-primary to-blue-600 text-primary-foreground py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full mb-4">
                <Icon name="ShoppingCart" size={20} />
                <span className="font-body font-medium">QUOTE CART</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
                Your Shipping Quotes
              </h1>
              <p className="text-xl text-primary-foreground/90 font-body">
                Review and finalize your international shipping services
              </p>
            </div>
          </div>
        </section>

        {/* Cart Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {cartItems?.length === 0 ? (
              /* Empty Cart */
              (<div className="text-center py-20">
                <div className="w-32 h-32 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Package" size={64} className="text-muted-foreground" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
                  Your Quote Cart is Empty
                </h2>
                <p className="text-muted-foreground font-body mb-8">
                  Browse our shipping services and add quotes to your cart
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate('/menu-catalog')}
                  iconName="Package"
                  iconPosition="left"
                >
                  Browse Services
                </Button>
              </div>)
            ) : (
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Cart Items */}
                <div className="lg:col-span-2 space-y-4">
                  {cartItems?.map(item => (
                    <div key={item?.id} className="bg-card rounded-xl shadow-warm p-6">
                      <div className="flex gap-6">
                        <img
                          src={item?.image}
                          alt={item?.imageAlt}
                          className="w-32 h-32 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="text-lg font-heading font-bold text-foreground">
                                {item?.name}
                              </h3>
                              <p className="text-sm text-muted-foreground">{item?.subtitle}</p>
                            </div>
                            <button
                              onClick={() => handleRemoveItem(item?.id)}
                              className="text-destructive hover:text-destructive/80"
                            >
                              <Icon name="Trash2" size={20} />
                            </button>
                          </div>
                          <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2 text-sm">
                              <Icon name="MapPin" size={16} className="text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {item?.origin} → {item?.destination}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Icon name="Package" size={16} className="text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {item?.weight} | {item?.dimensions}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Icon name="Clock" size={16} className="text-muted-foreground" />
                              <span className="text-muted-foreground">{item?.deliveryTime}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleQuantityChange(item?.id, item?.quantity - 1)}
                                className="w-8 h-8 rounded-lg border border-border hover:bg-muted flex items-center justify-center"
                              >
                                <Icon name="Minus" size={16} />
                              </button>
                              <span className="font-body font-medium">{item?.quantity}</span>
                              <button
                                onClick={() => handleQuantityChange(item?.id, item?.quantity + 1)}
                                className="w-8 h-8 rounded-lg border border-border hover:bg-muted flex items-center justify-center"
                              >
                                <Icon name="Plus" size={16} />
                              </button>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-heading font-bold text-primary">
                                ${(item?.price * item?.quantity)?.toFixed(2)}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                ${item?.price?.toFixed(2)} each
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Order Summary */}
                <div className="lg:col-span-1">
                  <div className="bg-card rounded-xl shadow-warm p-6 sticky top-24">
                    <h2 className="text-xl font-heading font-bold text-foreground mb-6">
                      Quote Summary
                    </h2>
                    
                    {/* Promo Code */}
                    <div className="mb-6">
                      <label className="text-sm font-body font-medium text-foreground mb-2 block">
                        Promo Code
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e?.target?.value)}
                          placeholder="Enter code"
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleApplyPromo}
                        >
                          Apply
                        </Button>
                      </div>
                      {appliedPromo && (
                        <p className="text-sm text-success mt-2 flex items-center gap-1">
                          <Icon name="Check" size={14} />
                          {appliedPromo?.description} applied
                        </p>
                      )}
                    </div>

                    {/* Price Breakdown */}
                    <div className="space-y-3 mb-6 pb-6 border-b border-border">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="font-body font-medium">${calculateSubtotal()?.toFixed(2)}</span>
                      </div>
                      {appliedPromo && (
                        <div className="flex justify-between text-sm">
                          <span className="text-success">Discount</span>
                          <span className="font-body font-medium text-success">
                            -${calculateDiscount()?.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Insurance (2%)</span>
                        <span className="font-body font-medium">${calculateInsurance()?.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Total */}
                    <div className="flex justify-between items-center mb-6">
                      <span className="text-lg font-heading font-bold text-foreground">Total</span>
                      <span className="text-2xl font-heading font-bold text-primary">
                        ${calculateTotal()?.toFixed(2)}
                      </span>
                    </div>

                    {/* Checkout Button */}
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleCheckout}
                      iconName="ArrowRight"
                      iconPosition="right"
                      fullWidth
                    >
                      Proceed to Booking
                    </Button>

                    <p className="text-xs text-muted-foreground text-center mt-4">
                      Final pricing confirmed after booking details
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default ShoppingCart;